type Listener<Payload> = (payload: Payload) => void;

export class EventEmitter<Events extends Record<string, unknown>> {
  // Internal storage for event listeners
  private listeners: {
    [K in keyof Events]?: Listener<Events[K]>[];
  } = {};

  /**
   * Adds a listener for the specified event type.
   * @param eventName - The name of the event to listen for.
   * @param callback - The callback function to invoke when the event is emitted.
   */
  public addListener<K extends keyof Events>(eventName: K, callback: Listener<Events[K]>): void {
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }
    this.listeners[eventName]?.push(callback);
  }

  /**
   * Emits an event, invoking all registered listeners for that event type.
   * @param eventName - The name of the event to emit.
   * @param payload - The data associated with the event.
   */
  public emit<K extends keyof Events>(eventName: K, payload: Events[K]): void {
    const eventListeners = this.listeners[eventName];
    if (eventListeners) {
      // Create a copy to prevent issues if listeners are modified during iteration
      [...eventListeners].forEach((listener) => {
        listener(payload);
      });
    }
  }

  /**
   * Removes a specific listener for an event type.
   * @param eventName - The name of the event.
   * @param callback - The callback function to remove.
   */
  public removeListener<K extends keyof Events>(eventName: K, callback: Listener<Events[K]>): void {
    const eventListeners = this.listeners[eventName];
    if (eventListeners) {
      this.listeners[eventName] = eventListeners.filter((listener) => listener !== callback);
    }
  }

  /**
   * Removes all listeners for a specific event type.
   * @param eventName - The name of the event.
   */
  public removeAllListeners<K extends keyof Events>(eventName: K): void {
    if (this.listeners[eventName]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.listeners[eventName];
    }
  }

  public destroy(): void {
    Object.keys(this.listeners).forEach((eventName) => {
      this.removeAllListeners(eventName);
    });
  }
}
